// Coordination landing (/coordination).
//
// Top bar, left to right: Create Project (existing CreateModal flow,
// projectAdd-gated) → project dropdown → Import Projects (existing
// CSV import flow, POST /projects/csv) → quick links (full dashboard /
// plan / partners) for the selected project.
// Body: the selected project's coordination gantt (read-only, same
// GET /projects/:id/gantt feed the full dashboard uses) and the
// project-scoped issue analytics panel (live /issue/analytics data —
// status/priority/trend/top-creators from the real project + users).
//
// Single-project companies get their one project auto-selected (the
// old behavior auto-forwarded to /coordination/:id; the dropdown
// landing supersedes that).
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, Empty, Select, Skeleton, Space, Tabs, Typography, message } from "antd";
import { AppstoreOutlined,
  ApartmentOutlined, FileSearchOutlined, PlusOutlined, ScheduleOutlined, TeamOutlined, UploadOutlined, UsergroupAddOutlined, CheckSquareOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { url, tokenRef } from "api";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
// The full admin create-project flow (title, number w/ validation,
// description, milestones, users, …) — not the slim legacy modal.
import CreateModal from "pages/configuration/addProjectModal";
import CoordinationGantt from "components/CoordinationGantt/CoordinationGantt";
import { progressEditRule } from "components/CoordinationGantt/ProgressCell";
import LookaheadRunCard from "components/Coordination/LookaheadRunCard";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import useLastProject from "components/Coordination/useLastProject";
import IssueAnalyticsChart from "pages/Issue/components/IssueAnalyticsChart";
import useProjectAccess from "hooks/useProjectAccess";
import ContractorMappingModal from "components/Coordination/ContractorMappingModal";
import GlanceTab from "components/Coordination/Glance/GlanceTab";
import { matchOption } from "components/SearchSelect/searchSelect";

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` };
}

export default function CoordinationIndexPage() {
  const history = useHistory();
  // "At a Glance" sits first, but a project opens on Schedule & Issues
  // (the owner's call). ?tab= keeps the choice.
  const location = useLocation();
  const activeTab = (() => { try { return new URLSearchParams(location.search).get("tab") === "glance" ? "glance" : "schedule"; } catch (e) { return "schedule"; } })();
  const setTab = (key) => {
    const params = new URLSearchParams(location.search);
    params.set("tab", key);
    history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
  };
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const usersPermissionRedux = useSelector((s) => s.user.userPermission);
  const companyPermissionRedux = useSelector((s) => s.user.companyPermission);
  const allPermissionRedux = useSelector((s) => s.user.allPermission);
  const FinalPermissions = allPermissionRedux
    ?.filter((element) => companyPermissionRedux?.includes(element))
    ?.filter((i) => usersPermissionRedux?.includes(i));
  const canAddProject =
    userInfoRedux?.admin ||
    userInfoRedux?.companyAdmin ||
    FinalPermissions?.includes(ALL_PERMISSIONS.PROJECT_ADD);

  const [projects, setProjects] = useState(null);
  // Remember the last project across visits (shared coordination memory:
  // per environment + per user, see helpers/projectSelection); ?projectId=
  // in the URL wins for this visit.
  const [projectId, setProjectId] = useLastProject(projects);
  const [feed, setFeed] = useState(null);
  const [feedLoading, setFeedLoading] = useState(false);
  const [modal, setModal] = useState("");
  const [importing, setImporting] = useState(false);
  const [contractorModal, setContractorModal] = useState(false);
  const fileInputRef = React.useRef(null);

  const loadProjects = () => {
    fetch(`${url}/projects/picker`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        // Picker responds { projects: [...] }; tolerate a bare array too.
        const list = Array.isArray(rows) ? rows : (rows?.projects || []);
        setProjects(list);
        // Auto-select: keep current selection if still present, else
        // first project (covers the single-project company case).
        setProjectId((cur) =>
          cur && list.some((p) => p._id === cur) ? cur : list[0]?._id || ""
        );
      })
      .catch(() => setProjects([]));
  };

  useEffect(() => { loadProjects(); }, []); // eslint-disable-line

  const loadFeed = (pid) => {
    if (!pid) { setFeed(null); return; }
    setFeedLoading(true);
    fetch(`${url}/projects/${pid}/gantt`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : null))
      .then(setFeed)
      .catch(() => setFeed(null))
      .finally(() => setFeedLoading(false));
  };
  useEffect(() => { loadFeed(projectId); }, [projectId]); // eslint-disable-line

  // Import Schedule: P6 XER / MS Project–P6 XML / Excel / CSV parsed
  // client-side (helpers/schedule/importSchedule.js — same approach as
  // the estimation gantt's Excel import) into plan items for the
  // SELECTED project via the existing gantt-items/bulk endpoint, then
  // the chart below refreshes from the same feed the full dashboard
  // uses. Nothing about the existing Plan Builder flow changes — this
  // is just another way to fill the same plan.
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!projectId) return message.warning("Select a project first");
    setImporting(true);
    try {
      const { parseScheduleFile } = await import("helpers/schedule/importSchedule");
      const { rows, deps, truncated, source } = await parseScheduleFile(file, { projectId });
      const res = await fetch(`${url}/projects/${projectId}/gantt-items/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ items: rows }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);

      // Second pass: schedule logic ties (TASKPRED / PredecessorLink /
      // Relationship). The bulk response returns created items in
      // submission order, so row indexes map 1:1 onto real ids.
      let depCount = 0;
      if (deps?.length && Array.isArray(body.items)) {
        const bySuccessor = new Map();
        for (const d of deps) {
          if (!bySuccessor.has(d.to)) bySuccessor.set(d.to, []);
          bySuccessor.get(d.to).push(body.items[d.from]?._id);
        }
        for (const [to, predIds] of bySuccessor) {
          const gid = body.items[to]?._id;
          const clean = predIds.filter(Boolean);
          if (!gid || !clean.length) continue;
          await fetch(`${url}/projects/${projectId}/gantt-items/${gid}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", ...auth() },
            body: JSON.stringify({ dependencies: clean }),
          }).then((r) => { if (r.ok) depCount += clean.length; });
        }
      }
      message.success(
        `${body.created} schedule activit${body.created === 1 ? "y" : "ies"} imported from ${source}` +
        (depCount ? `, ${depCount} dependency tie(s)` : "") +
        (truncated ? " (capped at 500 rows)" : "")
      );
      loadFeed(projectId);
    } catch (err) {
      message.error(err?.message || "Schedule import failed");
    } finally {
      setImporting(false);
    }
  };

  const selectedProject = (projects || []).find((p) => p._id === projectId);

  // Owner-vs-partner + CRUD keys for the selected project (server-side
  // truth): partners see the plan read-only - no Plan Builder / Schedule
  // Builder / import entry points at all.
  const { can, canEditPlan } = useProjectAccess(projectId);
  const canImportSchedule = !!can.ganttAdd;

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <CoordinationBreadcrumbs />
      <Typography.Title level={3} style={{ marginBottom: 4 }}>
        Coordination
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Plan, issues and schedule health per project.
      </Typography.Paragraph>

      <Space wrap style={{ marginBottom: 16 }}>
        {canAddProject && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal("CreateModal")}>
            Create Project
          </Button>
        )}
        <Select filterOption={matchOption}
          style={{ minWidth: 280 }}
          showSearch
          optionFilterProp="label"
          placeholder={projects === null ? "Loading projects…" : "Select a project"}
          value={projectId || undefined}
          onChange={setProjectId}
          options={(projects || []).map((p) => ({
            value: p._id,
            label: p.title || p.name || "Untitled",
          }))}
        />
        {canImportSchedule && (
          <input ref={fileInputRef} type="file" accept=".xer,.xml,.mpp"
                 style={{ display: "none" }} onChange={onImportFile} />
        )}
        {projectId && (
          <>
            <Button icon={<ScheduleOutlined />}
                    onClick={() => history.push(`/coordination/${projectId}`)}>
              Full dashboard
            </Button>
            <Button icon={<TeamOutlined />}
                    onClick={() => history.push(`/partners?projectId=${projectId}`)}>
              Partners
            </Button>
            {/* Field tracking - docs/tracking.md */}
            <Button icon={<CheckSquareOutlined />}
                    onClick={() => history.push(`/tracking/${projectId}`)}>
              Tracking
            </Button>
            {/* Areas, NEXT TO TRACKING (owner, 2026-09-24). It was already
                in the drawer and the coordination tab strip, and missing
                only here - which is the one place this page's own header
                promises it: "Plan, issues and schedule health per
                project". */}
            <Button icon={<AppstoreOutlined />}
                    onClick={() => history.push(`/areas/${projectId}`)}>
              Levels &amp; Areas
            </Button>
          </>
        )}
      </Space>

      {projects !== null && projects.length === 0 && (
        <Empty description={
          canAddProject
            ? "No projects yet — create or import one to start coordinating."
            : "No projects yet."
        } />
      )}

      {projectId && (
        <Tabs
          activeKey={activeTab}
          onChange={setTab}
          items={[
            {
              key: "glance",
              label: "At a Glance",
              // Keyed by project so switching the dropdown starts clean.
              children: <GlanceTab key={projectId} projectId={projectId} onOpenDashboard={(ganttItemId) => (ganttItemId ? history.push(`/coordination/${projectId}?tab=dashboard&focus=${ganttItemId}`) : setTab("schedule"))} />,
            },
            {
              key: "schedule",
              label: "Schedule & Issues",
              children: (
                <>
                  <Card size="small" title={`Schedule — ${selectedProject?.title || ""}`}
                        style={{ marginBottom: 16 }}
                        extra={
                          canEditPlan ? (
                            <Space wrap size={6}>
                              {/* Schedule actions live with the schedule: open the
                                  Schedule page (edit the existing one, or build a new
                                  one - WBS, durations, predecessors, partners,
                                  templates), or import one. Owner-company users with
                                  ganttAdd / ganttEdit only - partner companies see the
                                  schedule read-only. */}
                              {(can.ganttEdit || can.ganttAdd) && (
                                <Button size="small" icon={<ApartmentOutlined />}
                                        onClick={() => history.push(`/scheduler/${projectId}`)}
                                        title="Open the schedule for editing - activities, durations, predecessors, partners, templates">
                                  {feed?.items?.length ? "Edit schedule" : "Build schedule"}
                                </Button>
                              )}
                              {canImportSchedule && (
                                <Button size="small" type="primary" ghost icon={<FileSearchOutlined />}
                                        title="Import PDF meeting minutes, CSV or Excel trackers: schedule activities & milestones, issues / RFIs, checklists, minutes - with a field mapping you confirm"
                                        onClick={() => history.push(`/schedule-import/${projectId}`)}>
                                  Import documents
                                </Button>
                              )}
                              {canImportSchedule && (
                                <Button size="small" icon={<UploadOutlined />} loading={importing}
                                        title="Quick import of a P6 XER / MS Project XML schedule (no mapping step)"
                                        onClick={() => fileInputRef.current?.click()}>
                                  Import XER/XML
                                </Button>
                              )}
                              {(can.ganttEdit || can.ganttAdd) && (
                                <Button size="small" icon={<UsergroupAddOutlined />}
                                        title="Map the contractor shorthand used by imported documents (BES, DP, ...) to companies and people on this project"
                                        onClick={() => setContractorModal(true)}>
                                  Map contractors
                                </Button>
                              )}
                            </Space>
                          ) : (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>View only</Typography.Text>
                          )
                        }>
                    {/* A contractor look-ahead read from the last upload and
                        waiting for a yes. It draws nothing when there is
                        nothing waiting. */}
                    <LookaheadRunCard projectId={projectId} canApply={!!canEditPlan} onApplied={() => loadFeed(projectId)} />
                    {feedLoading ? (
                      <Skeleton active paragraph={{ rows: 4 }} />
                    ) : feed?.items?.length ? (
                      <CoordinationGantt
                        items={feed.items}
                        projectId={projectId}
                        issues={feed.issues || []}
                        checklistItems={feed.checklistItems || []}
                        areasByItem={feed.areasByItem || {}}
                        onEditItem={can.ganttEdit ? (item) => history.push(`/scheduler/${projectId}?focus=${item._id}`) : undefined}
                        canEditProgress={progressEditRule(can, userInfoRedux?.company)}
                      />
                    ) : (
                      <Empty description={
                        canEditPlan ? (
                          <span>
                            No schedule yet —{" "}
                            <a onClick={() => history.push(`/scheduler/${projectId}`)}>
                              build the schedule
                            </a>{" "}or import one above.
                          </span>
                        ) : (
                          <span>No schedule yet.</span>
                        )
                      } />
                    )}
                  </Card>

                  <Card size="small" title="Issue analytics">
                    <IssueAnalyticsChart projectId={projectId} />
                  </Card>
                </>
              ),
            },
          ]}
        />
      )}

      <CreateModal
        isOpen={modal === "CreateModal"}
        toggle={() => { setModal(""); loadProjects(); }}
      />
      <ContractorMappingModal open={contractorModal} onClose={() => setContractorModal(false)} projectId={projectId}
        canEdit={!!(can.ganttEdit || can.ganttAdd)} onChanged={() => { if (projectId) loadFeed && loadFeed(projectId); }} />
    </div>
  );
}
